// ./src/components/features/Coupons/CouponsFilterBar/CouponsFilterBar.tsx
//
// Filter controls for an event's issued coupons: by voucher type, lifecycle
// status, and who holds it. Fully controlled — the page owns the value and maps
// it to a CouponFilter. Rendered inline on tablet/desktop and inside the mobile
// FilterDrawer (bound to a draft), so any Select must portal its dropdown.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

export type CouponStatusFilter = "all" | "issued" | "redeemed" | "void";
export type CouponAssigneeFilter = "all" | "walkin" | "guest" | "group";

export interface CouponsFilterValue {
  couponTypeId: string | null;
  status: CouponStatusFilter;
  assignee: CouponAssigneeFilter;
}

export interface CouponsFilterBarProps {
  value: CouponsFilterValue;
  onChange: (value: CouponsFilterValue) => void;
  /** The account's reusable vouchers, as Select options. */
  typeOptions: { value: string; label: string }[];
  disabled?: boolean;
  /** The drawer owns Reset, so it hides the bar's own Clear. */
  showClear?: boolean;
}

export const EMPTY_COUPONS_FILTER: CouponsFilterValue = {
  couponTypeId: null,
  status: "all",
  assignee: "all",
};

export const couponsFilterActiveCount = (value: CouponsFilterValue): number =>
  [value.couponTypeId, value.status !== "all" ? value.status : null, value.assignee !== "all" ? value.assignee : null]
    .filter(Boolean).length;

const CouponsFilterBarComponent: React.FC<CouponsFilterBarProps> = ({
  value,
  onChange,
  typeOptions,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["coupons", "common"]);

  const active = couponsFilterActiveCount(value) > 0;

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={{ flex: "1 1 200px", minWidth: 180 }}>
        <Input.Wrapper label={t("coupons:filters.voucher", { defaultValue: "Voucher" })}>
          <SelectField
            mt={4}
            size="md"
            data={typeOptions}
            value={value.couponTypeId}
            onChange={(_name, next) => onChange({ ...value, couponTypeId: next })}
            placeholder={t("coupons:filters.allVouchers", { defaultValue: "All vouchers" })}
            comboboxProps={DRAWER_COMBOBOX_PROPS}
            disabled={disabled}
            clearable
            searchable
          />
        </Input.Wrapper>
      </Box>

      <Box style={{ flex: "1 1 260px", minWidth: 240 }}>
        <Input.Wrapper label={t("coupons:filters.status", { defaultValue: "Status" })}>
          <SegmentedControl
            fullWidth
            mt={4}
            size="md"
            value={value.status}
            onChange={(next) => onChange({ ...value, status: next as CouponStatusFilter })}
            disabled={disabled}
            data={[
              { value: "all", label: t("coupons:filters.all", { defaultValue: "All" }) },
              { value: "issued", label: t("coupons:status.issued", { defaultValue: "Issued" }) },
              { value: "redeemed", label: t("coupons:status.redeemed", { defaultValue: "Redeemed" }) },
              { value: "void", label: t("coupons:status.void", { defaultValue: "Void" }) },
            ]}
          />
        </Input.Wrapper>
      </Box>

      <Box style={{ flex: "1 1 260px", minWidth: 240 }}>
        <Input.Wrapper label={t("coupons:filters.givenTo", { defaultValue: "Given to" })}>
          <SegmentedControl
            fullWidth
            mt={4}
            size="md"
            value={value.assignee}
            onChange={(next) => onChange({ ...value, assignee: next as CouponAssigneeFilter })}
            disabled={disabled}
            data={[
              { value: "all", label: t("coupons:filters.all", { defaultValue: "All" }) },
              { value: "walkin", label: t("coupons:kind.walkin", { defaultValue: "Walk-in" }) },
              { value: "guest", label: t("coupons:kind.guest", { defaultValue: "Guest" }) },
              { value: "group", label: t("coupons:kind.group", { defaultValue: "RSVP group" }) },
            ]}
          />
        </Input.Wrapper>
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_COUPONS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const CouponsFilterBar = React.memo(CouponsFilterBarComponent);
